import React from 'react';
import useTranslate from '../../../customHooks/useTranslate';
import { useODataMultipleQueries } from '../../../customHooks/useODataMultipleQueries';
import Grid from '../../../muiComponents/Grid';
import GridChild from '../../../muiComponents/GridChild';
import LoadedOptionsTypeahead from '../../../muiComponents/LoadedOptionsTypeahead';
import TextInput from '../../../muiComponents/TextInput/TextInput';
import FiltersBar from '../../../Table/FiltersBar';
import { readOnlyEntities } from '../../../utils/commonVariables/entityKeys';
import {
    useTaxesComplementaryData,
    useTaxesList,
} from '../../contextsProperty/ContextTaxes';
import { IFilterOptionState } from '../../../types/lists/listTypes';
import {
    filterDoubleColumnGrid,
    filterSingleColumnGrid,
} from '../../../utils/taxes/taxesListUtils';
import Button from '../../../muiComponents/Button';
import { useSearchParams } from '../../../customHooks/useSearchParams';
import DateInput from '../../../muiComponents/DateInput';
import ChangeViewButtonBar from '../../../Table/ChangeViewButtonBar';
import { TaxListViewType } from '../../../types/taxes/taxesTypes';
import { commonIcons } from '../../../utils/commonVariables/commonIcons';
import {
    gridSpacing10,
    gridSpacing15,
} from '../../../utils/commonVariables/grid';
import useUserProfile from '../../../customHooks/useUserProfile';
import Tooltip from '../../../muiComponents/Tooltip';

const TaxesFiltersBar = () => {
    const {
        handleFilterChange,
        filters,
        handleSearch,
        resetFilters,
        isLoading,
        currentResults,
        totalResults,
        visibleView,
        changeVisibleView,
    } = useTaxesList();
    const { supplierHotels } = useTaxesComplementaryData();
    const translate = useTranslate();
    const { readOnlyUser } = useUserProfile();

    const supplierHotelSelected = filters.IdSupplierHotel
        .value as IFilterOptionState;
    const IdSupplierHotel = supplierHotelSelected?.Id
        ? parseInt(supplierHotelSelected?.Id)
        : null;

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!supplierHotelSelected,
    });

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleAddTax = () => {
        setSearchParam('id', 'new');
    };

    const buttonsButtonBar = [
        {
            icon:
                visibleView === TaxListViewType.CALENDAR
                    ? commonIcons.LIST
                    : commonIcons.CALENDAR,

            label:
                visibleView === TaxListViewType.CALENDAR ? 'list' : 'calendar',
            view:
                visibleView === TaxListViewType.CALENDAR
                    ? TaxListViewType.TABLE
                    : TaxListViewType.CALENDAR,
        },
    ];

    return (
        <FiltersBar
            title='taxes'
            clearFilters={resetFilters}
            search={async () => await handleSearch(1)}
            isLoading={isLoading}
            currentResults={currentResults}
            totalResults={totalResults}
            startButtonBar={
                <Tooltip
                    titleText='read_only_taxes'
                    hideTooltip={!readOnlyUser}
                >
                    <span>
                        <Button
                            variant='contained'
                            startIcon='Add'
                            onClick={handleAddTax}
                            disabled={readOnlyUser}
                        >
                            {translate('add')}
                        </Button>
                    </span>
                </Tooltip>
            }
            endButtonBar={
                <ChangeViewButtonBar
                    changeVisibleView={(view: string) =>
                        changeVisibleView(view as TaxListViewType)
                    }
                    buttons={buttonsButtonBar}
                />
            }
        >
            <Grid
                className='flex-grow'
                columnSpacing={gridSpacing10}
                rowSpacing={gridSpacing15}
            >
                <GridChild gridProps={filterDoubleColumnGrid}>
                    <TextInput
                        labelText={'name'}
                        id={'name'}
                        value={filters.Name.value || ''}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: string
                        ) => handleFilterChange('Name', newValue)}
                        onKeyPress={async (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                await handleSearch(1);
                            }
                        }}
                    />
                </GridChild>

                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'hotel'}
                        options={supplierHotels.data || []}
                        value={filters.IdSupplierHotel.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) => handleFilterChange('IdSupplierHotel', newValue)}
                        disabled={supplierHotels.isLoading}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'room'}
                        options={supplierHotelRoomTypes.data || []}
                        value={filters.IdSupplierHotelRoomType.value || null}
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRoomType',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'meal_plan'}
                        options={supplierHotelMealPlans.data || []}
                        value={filters.IdSupplierHotelMealPlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelMealPlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'rate_plan'}
                        options={supplierHotelRatePlans.data || []}
                        value={filters.IdSupplierHotelRatePlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRatePlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.FromDate.value}
                        labelText={'from'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('FromDate', newDate);
                        }}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.ToDate.value}
                        labelText={'to'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('ToDate', newDate);
                        }}
                    />
                </GridChild>
            </Grid>
        </FiltersBar>
    );
};

export default TaxesFiltersBar;
